/**
 * A hand-built, pure parser for the AAMVA DL/ID card-design-standard tagged-field text payload a
 * PDF417 barcode decoder hands back (`BACKLOG.md` C19, `docs/decisions/patient-license-scan-scope.md`
 * §1). This module never touches image data, a camera, or a barcode decoder itself — the client-side
 * capture screen (`web/src/routes/kiosk/LicenseScanCapturePage.tsx`) decodes the PDF417 symbol with
 * `zxing-wasm` and hands the resulting raw text straight to `parseAamvaLicenseBarcode` below, entirely
 * in the browser; only the return value of this function ever reaches the server.
 *
 * "The text-parsing half is genuinely simple and should be hand-built, not a dependency" (the
 * decision doc's own §1) — this is deterministic string parsing against a fixed, publicly-documented
 * tagged-field format, not machine vision, so it belongs as its own small pure/tested module per
 * `claude-kit/rules/invariants.md`'s "business rules live in pure, tested modules."
 *
 * Deliberately narrow: only the eight demographic subfields `licenseScanFields.ts`'s
 * `licenseScanExtractedFieldsSchema` has a field for (`DAC`/`DCS` name, `DBB` date of birth, `DBC`
 * sex, `DAG`/`DAI`/`DAJ`/`DAK` address). Every other AAMVA subfile designator a real license carries —
 * license number (`DAQ`), class (`DCA`), expiration (`DBA`), height, eye color, and so on — is read
 * past and discarded, never surfaced in the returned value: the decision doc's own §5 minimum-
 * necessary-data argument (no license number, class, or expiration column on this feature's table, so
 * there is no reason to carry those values past this parse step even transiently).
 *
 * Fails closed, field by field: an unparseable or out-of-range value (a malformed date of birth, an
 * unrecognized sex code, a value longer than this schema's own bound) is simply omitted from the
 * result rather than thrown — the same `safeParse`-and-degrade posture `kiosk/service.ts`'s
 * `resolveKioskConsentDocumentTypes` already takes toward untrusted structured input, applied here to
 * a barcode this build did not produce and cannot fully trust to be well-formed (a damaged scan, a
 * pre-AAMVA-compliant card, a foreign ID that happens to share the PDF417 symbology). A field this
 * parser could not confidently extract is a field the review UI shows as blank, never a wrong guess.
 */

import {
  licenseScanExtractedFieldsSchema,
  type LicenseScanExtractedFields,
} from './licenseScanFields.ts';

/**
 * AAMVA data-element ID -> the field on `LicenseScanExtractedFields` it maps to. The full AAMVA D20
 * tag set has dozens of entries; this is deliberately only the subset this feature has a use for
 * (this file's own header explains why the rest are never mapped at all, not just unused).
 */
const TAG_FIELD_MAP: Readonly<Record<string, keyof LicenseScanExtractedFields>> = {
  DAC: 'firstName',
  DCS: 'lastName',
  DBB: 'dateOfBirth',
  DBC: 'administrativeSex',
  DAG: 'addressLine1',
  DAI: 'city',
  DAJ: 'state',
  DAK: 'postalCode',
};

/** `1` male, `2` female, `9` not specified — AAMVA D20's own closed sex-code vocabulary, mapped onto
 * `@emr/shared`'s `ADMINISTRATIVE_SEX_VALUES`. Any other value (a corrupted read, a pre-D20 card using
 * a different scheme) maps to nothing rather than a guess — AAMVA has no code for `'other'`, so
 * guessing one would be inventing data the card never claimed, not extracting it. */
function mapAamvaSex(code: string): 'male' | 'female' | 'unknown' | null {
  switch (code.trim()) {
    case '1':
      return 'male';
    case '2':
      return 'female';
    case '9':
      return 'unknown';
    default:
      return null;
  }
}

/**
 * `MMDDCCYY` (AAMVA D20's fixed date-of-birth format) to `calendarDateSchema`'s own `YYYY-MM-DD`.
 * `null` for anything that is not eight digits or does not resolve to a real calendar date —
 * `Date.UTC`'s own overflow behavior (e.g. day 31 in a 30-day month) is checked explicitly by reading
 * the constructed date back, rather than trusted, since `Date` silently rolls an out-of-range field
 * into the next one instead of refusing it.
 */
function parseAamvaDateOfBirth(raw: string): string | null {
  const trimmed = raw.trim();
  if (!/^\d{8}$/.test(trimmed)) return null;
  const month = Number(trimmed.slice(0, 2));
  const day = Number(trimmed.slice(2, 4));
  const year = Number(trimmed.slice(4, 8));
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;

  const asUtc = new Date(Date.UTC(year, month - 1, day));
  if (
    asUtc.getUTCFullYear() !== year ||
    asUtc.getUTCMonth() !== month - 1 ||
    asUtc.getUTCDate() !== day
  ) {
    return null;
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(year)}-${pad(month)}-${pad(day)}`;
}

/**
 * Every tag-prefixed element of a decoded AAMVA PDF417 payload — split on the record separator AAMVA
 * actually uses (`\n`), tolerating a stray leading/trailing `\r` from a CRLF-terminated element, the
 * shape a decoder that preserves the payload's own control characters hands back. The header block
 * (`@`, the AAMVA/ANSI compliance indicator, the subfile designator table) is never on a line whose
 * first three characters match a key in `TAG_FIELD_MAP`, so it is discarded for free by the tag
 * whitelist in `parseAamvaLicenseBarcode` below rather than needing its own explicit skip.
 */
function splitElements(raw: string): string[] {
  return raw.split('\n').map((line) => line.replace(/^\r/, '').replace(/\r$/, ''));
}

export function parseAamvaLicenseBarcode(raw: string): LicenseScanExtractedFields {
  const candidate: Partial<Record<keyof LicenseScanExtractedFields, string>> = {};

  for (const line of splitElements(raw)) {
    if (line.length < 3) continue;
    const tag = line.slice(0, 3);
    const field = TAG_FIELD_MAP[tag];
    if (field === undefined) continue;
    const value = line.slice(3).trim();
    if (value.length === 0) continue;

    if (field === 'dateOfBirth') {
      const parsedDate = parseAamvaDateOfBirth(value);
      if (parsedDate !== null) candidate[field] = parsedDate;
      continue;
    }
    if (field === 'administrativeSex') {
      const parsedSex = mapAamvaSex(value);
      if (parsedSex !== null) candidate[field] = parsedSex;
      continue;
    }
    candidate[field] = value;
  }

  // One schema check rather than validating each field inline above: this also enforces the shared
  // length bounds `licenseScanExtractedFieldsSchema` already carries, so a field this parser
  // extracted but that is implausibly long (a corrupted read repeating bytes) is dropped the same
  // fail-closed way a malformed one is. A single field failing that check must not discard every
  // other field the barcode did parse correctly, so an invalid whole-object result is reduced
  // field-by-field rather than returned wholesale or thrown away entirely.
  const wholeResult = licenseScanExtractedFieldsSchema.safeParse(candidate);
  if (wholeResult.success) return wholeResult.data;

  const safe: Partial<Record<keyof LicenseScanExtractedFields, string>> = {};
  for (const [key, value] of Object.entries(candidate)) {
    const singleResult = licenseScanExtractedFieldsSchema.safeParse({ [key]: value });
    if (singleResult.success) Object.assign(safe, singleResult.data);
  }
  return licenseScanExtractedFieldsSchema.parse(safe);
}
